import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import NeverOverpayNav from './NeverOverpayNav';
import NeverOverpayTimeline from './NeverOverpayTimeline';
import { neveroverpayApi } from './neveroverpayApi';
import { fieldLabel, outcomeLabel, stateLabel, workflowLabel, dollarsFromCents, replyLabel } from './neveroverpayCopy';
import { TrackerStages } from './RecoveryTracker';
import './neveroverpay.css';
import './neveroverpay-ui.css';

const REVIEW_STATES = ['READY_FOR_REVIEW', 'USER_NOTIFIED', 'ACTION_SELECTED', 'AWAITING_USER_DECISION', 'AWAITING_APPROVAL'];
const WAITING_STATES = ['WAITING_FOR_MERCHANT', 'OUTCOME_VERIFICATION', 'WAITING_FOR_VERIFICATION', 'EXECUTING'];
const RENEWAL_CHOICES = [
  { id: 'keep', label: 'Keep' },
  { id: 'downgrade', label: 'Downgrade' },
  { id: 'cancel', label: 'Cancel' },
  { id: 'negotiate', label: 'Negotiate' },
];

export default function NeverOverpayCase() {
  const { caseId } = useParams();
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState('');
  const [proof, setProof] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [sentNotice, setSentNotice] = useState('');
  const [tracker, setTracker] = useState(null);
  const messageRef = useRef(null);

  async function load() {
    try {
      setPayload(await neveroverpayApi.getCase(caseId));
      neveroverpayApi.caseTracker(caseId).then(setTracker).catch(() => {});
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => { load(); }, [caseId]);

  const caseData = payload?.case;
  const plan = caseData?.action_plan;
  const offer = caseData?.merchant_offer;
  const graphNodes = payload?.graph?.nodes || [];
  const timeline = payload?.graph?.timeline || [];
  const path = payload?.graph?.path || [];
  const overlapPending = caseData?.workflow === 'OVERLAPPING_SUBSCRIPTION' && !caseData?.facts?.overlap_confirmed;
  const members = caseData?.facts?.members || [];
  const keepChoice = !overlapPending && (plan?.channel === 'none' || caseData?.renewal_choice === 'keep');
  const noSendYet = keepChoice || overlapPending;
  const reviewable = caseData && REVIEW_STATES.includes(caseData.state);
  const review = caseData?.review || caseData?.recommendation?.review || {};
  const reviewParagraphs = review.paragraphs?.length ? review.paragraphs : [];
  const evidenceItems = review.evidence_items || [];
  const recommendedAction = review.recommended_action || caseData?.recommended_action || plan?.summary;
  const potentialDisplay = review.potential_display || review.amount_display || caseData?.estimated_display;
  const actionOptions = caseData?.action_options || caseData?.recommendation?.action_options || [];
  const executionPlan = caseData?.execution_plan || {};
  const selectedChoice = caseData?.facts?.action_choice || '';
  const constraints = executionPlan.executionConstraints || {};
  const disposition = caseData?.merchant_reply?.disposition || caseData?.facts?.merchant_disposition || '';
  const offerOpen = Boolean(offer && !['declined', 'dismissed', 'escalated'].includes(offer.status || ''));
  const showSentWatch = (sentNotice || caseData?.state === 'WAITING_FOR_MERCHANT') && !['pending', 'refund_reissued', 'confirmed'].includes(disposition) && !offerOpen;

  function scrollToMessage() {
    messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function chooseAction(option) {
    setBusy(true);
    try {
      await neveroverpayApi.selectAction(caseData.id, option, option === 'remind_later' ? 1 : 1);
      await load();
      setError('');
      if (option === 'send_email' || option === 'human_help') setApprovalOpen(true);
      if (option === 'draft_email') scrollToMessage();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function run(fn) {
    setBusy(true);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (!caseData && !error) {
    return <div className="np-root"><div className="np-wrap np-muted" style={{ padding: 40 }}>Loading this recovery…</div></div>;
  }

  return (
    <div className="np-root" data-testid="neveroverpay-case">
      <div className="np-wrap">
        <NeverOverpayNav mark="Recovery" />
        {error ? <div className="np-error" style={{ marginBottom: 16 }}>{error}</div> : null}
        {caseData ? (
          <div className="np-case-layout">
            <div>
              <div className="np-pill">{workflowLabel(caseData.workflow)}</div>
              {caseData.ai_used ? <span className="np-pill" style={{ marginLeft: 8 }}>AI read this email</span> : null}
              <h1 style={{ fontSize: 32, letterSpacing: '-0.03em', margin: '12px 0' }}>{caseData.title}</h1>
              <p className="np-muted">
                {caseData.merchant} · Order {caseData.order_number || '—'} · {stateLabel(caseData.state)}
                {caseData.confidence != null ? ` · ${Math.round(Number(caseData.confidence) * 100)}% confidence` : ''}
              </p>
              {caseData.snoozed_until ? (
                <p className="np-muted" style={{ marginTop: 8 }}>Remind later until {String(caseData.snoozed_until).slice(0, 16)}.</p>
              ) : null}

              {tracker ? (
                <div className={`np-tracker-card ${tracker.status}`} style={{ marginTop: 20 }} data-testid="case-recovery-tracker">
                  <div className="np-between">
                    <div>
                      <div className="np-kicker">Recovery tracker</div>
                      <strong>{tracker.status === 'won' ? 'Money back in bank' : tracker.status === 'closed' ? 'Closed' : tracker.stage_label}</strong>
                      <span className="np-muted"> · {tracker.progress_pct}% there · open {tracker.days_open} day{tracker.days_open === 1 ? '' : 's'}</span>
                    </div>
                    <span className={`np-tag ${tracker.status === 'won' ? 'green' : tracker.needs_you ? 'amber' : 'blue'}`}>{tracker.needs_you ? 'Needs your OK' : tracker.stage_label}</span>
                  </div>
                  <TrackerStages stages={tracker.stages} status={tracker.status} />
                  <div className="np-tracker-next"><span className="np-muted">Next:</span><strong>{tracker.next_step}</strong></div>
                </div>
              ) : null}

              <div className="np-card" style={{ marginTop: 24 }} data-testid="neveroverpay-review">
                <p className="np-muted" style={{ marginBottom: 4 }}>Potential recovery</p>
                <div className="np-stat np-money np-review-amount">{potentialDisplay}</div>
                <div className="np-review-copy">
                  {(reviewParagraphs.length ? reviewParagraphs : [caseData.recommendation?.explanation || caseData.why_now]).map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>
                {review.deadline_display ? (
                  <p style={{ marginTop: 12 }}><strong>{review.deadline_display}</strong></p>
                ) : null}
                <h3 style={{ marginTop: 24 }}>Evidence</h3>
                <ul className="np-evidence-list">
                  {evidenceItems.length ? evidenceItems.map((item) => (
                    <li key={item.id} className={item.uncertain || item.ok === false ? 'uncertain' : 'ok'}>
                      <span className="np-evidence-mark" aria-hidden="true">{item.ok && !item.uncertain ? '✓' : '?'}</span>
                      <span>
                        {item.label}
                        {item.quote ? <span className="np-muted"> — “{item.quote}”</span> : null}
                      </span>
                    </li>
                  )) : (caseData.evidence || []).slice(0, 8).map((span, idx) => (
                    <li key={`${span.field}-${idx}`} className="ok">
                      <span className="np-evidence-mark" aria-hidden="true">✓</span>
                      <span>
                        {fieldLabel(span.field)}
                        {span.quote ? <span className="np-muted"> — “{span.quote}”</span> : null}
                      </span>
                    </li>
                  ))}
                </ul>
                <h3 style={{ marginTop: 24 }}>Recommended action</h3>
                <p>{recommendedAction}</p>
                {review.why_recommended ? (
                  <p className="np-muted" style={{ marginTop: 8 }}>{review.why_recommended}</p>
                ) : null}
                {reviewable && !overlapPending && actionOptions.length ? (
                  <div className="np-action-options" data-testid="neveroverpay-action-options">
                    <h3 style={{ marginTop: 24 }}>Choose an action</h3>
                    <p className="np-muted">The planner builds a constrained plan only after you pick one. It cannot accept store credit, change a plan, or make a payment.</p>
                    {actionOptions.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`np-option ${selectedChoice === option.id ? 'on' : ''}`}
                        disabled={busy}
                        onClick={() => chooseAction(option.id)}
                      >
                        <strong>Option {option.option}: {option.label}</strong>
                        <span>{option.detail}</span>
                      </button>
                    ))}
                  </div>
                ) : reviewable && !overlapPending ? (
                  <div className="np-actions" style={{ marginTop: 16 }}>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={scrollToMessage}>
                      Review message
                    </button>
                    <button type="button" className="np-btn" disabled={busy} onClick={() => setApprovalOpen(true)}>
                      {keepChoice ? 'Confirm keep' : 'Approve & send'}
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.remindLater(caseData.id, 1))}>
                      Remind me later
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.dismiss(caseData.id))}>
                      Dismiss
                    </button>
                  </div>
                ) : null}
                {(review.uncertainty || []).length ? (
                  <div className="np-uncertainty">
                    <p><strong>Uncertain</strong></p>
                    <ul className="np-list">
                      {review.uncertainty.map((note) => <li key={note}>{note}</li>)}
                    </ul>
                  </div>
                ) : null}
                <p className="np-muted" style={{ marginTop: 12 }}>
                  {review.disclaimer || 'This is an estimate, not a promise that money will come back, and not a legal determination.'}
                </p>
                {reviewable && !overlapPending ? (
                  <div className="np-actions" style={{ marginTop: 16 }}>
                    {selectedChoice && selectedChoice !== 'remind_later' ? (
                      <button type="button" className="np-btn ghost" disabled={busy} onClick={scrollToMessage}>
                        Review message
                      </button>
                    ) : null}
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.dismiss(caseData.id))}>
                      Dismiss
                    </button>
                  </div>
                ) : null}
              </div>
              {caseData.facts?.follow_up ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-timeout">
                  <h3>Follow-up ready</h3>
                  <p>They did not reply in 5 days. This is a new request. The old approval no longer applies.</p>
                </div>
              ) : null}
              {showSentWatch ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Request sent</h3>
                  <p>We sent this request to {plan?.target || caseData.merchant}.</p>
                  <ul className="np-list">
                    <li>We will watch for a reply.</li>
                    <li>We will notify you if they need information.</li>
                    <li>If there is no reply in 5 days, we will ask you to approve a new follow-up.</li>
                    <li>We will verify the refund when possible.</li>
                  </ul>
                  <p className="np-muted">Expected response time: 1–5 business days.</p>
                </div>
              ) : null}
              {disposition === 'pending' && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-pending-reply">
                  <h3>Refund pending</h3>
                  <p>They said the refund will arrive in a few days. We set a follow-up timer. Savings are not counted until the bank shows the credit.</p>
                  {caseData.merchant_reply?.quote ? <p className="np-muted" style={{ marginTop: 8 }}>{caseData.merchant_reply.quote}</p> : null}
                </div>
              ) : null}
              {(caseData.state === 'WAITING_FOR_VERIFICATION' || ['refund_reissued', 'confirmed'].includes(disposition)) && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-reissued-reply">
                  <h3>Refund reissued — watching your bank</h3>
                  <p>The merchant said the refund was processed. We will not count savings until it posts on your connected account.</p>
                </div>
              ) : null}
              {caseData.facts?.needs_user_input && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-user-input">
                  <h3>They need information from you</h3>
                  <p>{offer?.prompt || 'The merchant asked for more information. We will not send extra details automatically.'}</p>
                  {offerOpen && offer?.type === 'user_input' ? (
                    <div className="np-actions">
                      <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'human_help'))}>
                        Request human help
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {offer?.type === 'denied' && offerOpen && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-denied-reply">
                  <h3>They denied the request</h3>
                  <p>{offer.reason || 'The merchant denied the refund. The case stays open until you choose what to do next.'}</p>
                  <p className="np-muted" style={{ marginTop: 8 }}>Classification: {replyLabel('denied')}</p>
                  <div className="np-actions">
                    <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'human_help'))}>
                      Request human help
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'dismiss'))}>
                      Dismiss this recovery
                    </button>
                  </div>
                </div>
              ) : null}
              {offer?.type === 'phone' && offerOpen && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-phone-reply">
                  <h3>They asked you to call</h3>
                  <p>We will not call automatically. Use this script, or request a human concierge.</p>
                  <div className="np-pre" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{offer.call_script}</div>
                  <div className="np-actions">
                    <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'human_help'))}>
                      Request human help
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'dismiss'))}>
                      I will handle this
                    </button>
                  </div>
                </div>
              ) : null}
              {caseData.pending_bank_confirm && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-bank-confirm">
                  <h3>Confirm this bank credit</h3>
                  <p>
                    A credit of ${dollarsFromCents(caseData.pending_bank_confirm.amount_cents)} may match this refund
                    (score {Number(caseData.pending_bank_confirm.score).toFixed(2)}). Confirm it before we count savings.
                  </p>
                  <p className="np-muted" style={{ marginTop: 8 }}>
                    Scores 0.90 and above verify automatically. 0.70–0.89 need your OK. Below 0.70 we keep watching.
                  </p>
                  <div className="np-actions">
                    <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.confirm(caseData.id, { state: 'verified' }))}>
                      Yes, this is the refund
                    </button>
                  </div>
                </div>
              ) : null}
              {caseData.state === 'RESOLVED' && caseData.outcome === 'RECOVERED' ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-verified">
                  <h3>Refund verified — {caseData.verified_display || caseData.estimated_display}</h3>
                  <p>{caseData.merchant} refunded {caseData.verified_display || caseData.estimated_display} to your connected account.</p>
                  <p style={{ marginTop: 12 }}>Case complete:</p>
                  <ul className="np-list np-checklist">
                    <li>Return received</li>
                    <li>Refund request sent</li>
                    <li>Merchant reissued refund</li>
                    <li>Refund verified through your bank activity</li>
                  </ul>
                  <p className="np-money" style={{ marginTop: 12 }}>Verified recovered value: {caseData.verified_display || caseData.estimated_display}</p>
                </div>
              ) : null}
              {offer?.type === 'store_credit' && offer.status !== 'declined' && caseData.state !== 'RESOLVED' ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>They offered store credit</h3>
                  <p>This is a decision you have to make. We will not accept credit from a keyword in their email.</p>
                  <div className="np-actions">
                    <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'accept_credit'))}>
                      Accept store credit
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'request_original'))}>
                      Refund original payment
                    </button>
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.offerDecision(caseData.id, 'decline'))}>
                      Decline offer
                    </button>
                  </div>
                </div>
              ) : null}

              {caseData.workflow === 'PRICE_INCREASE' && caseData.facts?.increase_percent != null ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Increase percentage</h3>
                  <p>
                    {caseData.facts.old_monthly_cents != null ? `$${dollarsFromCents(caseData.facts.old_monthly_cents)}` : 'old rate'}
                    {' → '}
                    {caseData.facts.new_monthly_cents != null ? `$${dollarsFromCents(caseData.facts.new_monthly_cents)}` : 'new rate'}
                    {' '}({Number(caseData.facts.increase_percent).toFixed(1)}%)
                  </p>
                  <p className="np-muted" style={{ marginTop: 8 }}>
                    (new − old) / old × 100. Rules calculate this. AI does not.
                  </p>
                </div>
              ) : null}

              {(payload.documents || []).map((doc) => (
                <div className="np-card" style={{ marginTop: 16 }} key={doc.id}>
                  <h3>{doc.filename}</h3>
                  <div className="np-pre">{doc.text}</div>
                </div>
              ))}
              {timeline.length || path.length || graphNodes.length ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Mail and bank, one timeline</h3>
                  <p className="np-muted">Gmail and Plaid are evidence on the same obligation — not two dashboards.</p>
                  <NeverOverpayTimeline timeline={timeline} path={path} />
                </div>
              ) : null}
              {(caseData.events || []).length ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Case history</h3>
                  {(caseData.events || []).map((event) => (
                    <p key={event.id} className="np-muted" style={{ marginTop: 8 }}>
                      {stateLabel(event.to_state)} · {event.reason} · {String(event.at || '').slice(0, 16)}
                    </p>
                  ))}
                </div>
              ) : null}
              {(caseData.agent_trace || []).length ? (
                <div className="np-card" style={{ marginTop: 16 }} data-testid="neveroverpay-agent-trail">
                  <h3>Specialists on this recovery</h3>
                  <p className="np-muted">Separate agents handled intake, documents, eligibility, ranking, and the draft. There is no autonomous super agent. Money, dates, permissions, and sending stay in backend rules.</p>
                  <div className="np-agent-trail">
                    {caseData.agent_trace.map((agent) => (
                      <span key={agent.agent_id} className={`np-agent-chip ${agent.kind === 'hybrid' ? 'hybrid' : ''}`}>
                        {agent.name || agent.agent_id}
                        {agent.decision ? ` · ${agent.decision}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
            <div>
              {caseData.workflow === 'RENEWAL_RESCUE' && reviewable ? (
                <div className="np-card" style={{ marginBottom: 16 }}>
                  <h3>What should we do?</h3>
                  <p className="np-muted">Keep sends nothing. The other choices draft an email you still have to approve.</p>
                  <div className="np-actions">
                    {RENEWAL_CHOICES.map((choice) => (
                      <button
                        key={choice.id}
                        type="button"
                        className={`np-btn ${caseData.renewal_choice === choice.id || (!caseData.renewal_choice && choice.id === 'cancel') ? '' : 'ghost'}`}
                        disabled={busy}
                        onClick={() => run(() => neveroverpayApi.renewalChoice(caseData.id, choice.id))}
                      >
                        {choice.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              {overlapPending && reviewable ? (
                <div className="np-card" style={{ marginBottom: 16 }}>
                  <h3>Are both of these intentional?</h3>
                  <p className="np-muted">
                    We will not draft a cancellation until you confirm. {members.length ? members.map((row) => row.merchant || row.merchant_slug).join(' and ') : caseData.merchant}.
                  </p>
                  <div className="np-actions">
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.overlapDecision(caseData.id, 'keep_both'))}>
                      Keep both
                    </button>
                    {members.length ? members.map((row) => (
                      <button
                        key={row.merchant_slug || row.merchant}
                        type="button"
                        className="np-btn"
                        disabled={busy}
                        onClick={() => run(() => neveroverpayApi.overlapDecision(caseData.id, 'cancel_one', row.merchant_slug))}
                      >
                        Cancel {row.merchant || row.merchant_slug}
                      </button>
                    )) : (
                      <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.overlapDecision(caseData.id, 'cancel_one'))}>
                        Cancel one
                      </button>
                    )}
                  </div>
                </div>
              ) : null}
              <div className="np-card" id="np-message" ref={messageRef}>
                <h3>{noSendYet ? 'What we will record' : selectedChoice === 'human_help' ? 'Specialist follow-up plan' : selectedChoice === 'draft_email' ? 'Draft for you to send' : 'The request we want to send'}</h3>
                <p className="np-muted">{plan?.summary}</p>
                {executionPlan.actionType ? (
                  <div className="np-plan-spec" data-testid="neveroverpay-execution-plan">
                    <p><strong>Action type:</strong> {executionPlan.actionType}</p>
                    <p><strong>Purpose:</strong> {executionPlan.purpose}</p>
                    {executionPlan.recipient ? <p><strong>Recipient:</strong> {executionPlan.recipient}</p> : null}
                    {(executionPlan.requiredEvidence || []).length ? (
                      <p><strong>Required evidence:</strong> {executionPlan.requiredEvidence.join(', ')}</p>
                    ) : null}
                    <ul className="np-list">
                      <li>Can send email: {constraints.canSendEmail ? 'yes' : 'no'}</li>
                      <li>Can accept store credit: {constraints.canAcceptStoreCredit ? 'yes' : 'no'}</li>
                      <li>Can accept settlement: {constraints.canAcceptSettlement ? 'yes' : 'no'}</li>
                      <li>Can change plan: {constraints.canChangePlan ? 'yes' : 'no'}</li>
                      <li>Can make payment: {constraints.canMakePayment ? 'yes' : 'no'}</li>
                      <li>Requires explicit approval: {executionPlan.requiresExplicitApproval ? 'yes' : 'no'}</li>
                    </ul>
                  </div>
                ) : null}
                {noSendYet ? (
                  <p style={{ marginTop: 12, fontSize: 13 }}>{overlapPending ? 'No merchant email is drafted until you confirm.' : 'No email will be sent.'}</p>
                ) : (
                  <>
                    <p style={{ marginTop: 12, fontSize: 13 }}>To: {plan?.target}</p>
                    {plan?.constraints?.form_url ? (
                      <p style={{ fontSize: 13 }}>Form: <a href={plan.constraints.form_url} target="_blank" rel="noreferrer">{plan.constraints.form_url}</a></p>
                    ) : null}
                    <p style={{ fontSize: 13 }}>Subject: {plan?.subject}</p>
                    <div className="np-pre" style={{ marginTop: 12 }}>{plan?.body}</div>
                  </>
                )}
                <p className="np-muted" style={{ marginTop: 10 }}>
                  Your OK applies to this exact {keepChoice ? 'choice' : 'email'} only. If the wording changes, you must approve it again.
                  We will not accept a new plan, change a subscription, or make a payment without a fresh approval.
                </p>
                <div className="np-actions">
                  {reviewable && !overlapPending ? (
                    <>
                      <button type="button" className="np-btn ghost" disabled={busy} onClick={scrollToMessage}>
                        Review message
                      </button>
                      <button type="button" className="np-btn" disabled={busy} onClick={() => setApprovalOpen(true)}>
                        {keepChoice ? 'Confirm keep' : selectedChoice === 'human_help' ? 'Approve specialist follow-up' : selectedChoice === 'draft_email' ? 'Approve this draft' : 'Approve & send'}
                      </button>
                      <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.remindLater(caseData.id, 1))}>
                        Remind me later
                      </button>
                    </>
                  ) : null}
                  {caseData.state === 'APPROVED' ? (
                    <>
                      {plan?.channel === 'email' && plan?.target?.includes('@') ? (
                        <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.sendApproved(caseData.id))}>
                          Send it now
                        </button>
                      ) : (
                        <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.completeTask(caseData.id))}>
                          {keepChoice ? 'Confirm I am keeping this plan' : 'I sent this from my own email / form'}
                        </button>
                      )}
                      <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.markSent(caseData.id))}>
                        I already emailed them
                      </button>
                    </>
                  ) : null}
                  {WAITING_STATES.includes(caseData.state) ? (
                    <>
                      <button type="button" className="np-btn" disabled={busy} onClick={() => run(() => neveroverpayApi.confirm(caseData.id, { state: 'confirmed' }))}>
                        They said yes
                      </button>
                    </>
                  ) : null}
                  {caseData.state !== 'RESOLVED' && !(reviewable && !overlapPending) ? (
                    <button type="button" className="np-btn ghost" disabled={busy} onClick={() => run(() => neveroverpayApi.dismiss(caseData.id))}>
                      Dismiss
                    </button>
                  ) : caseData.state === 'RESOLVED' ? (
                    <div className="np-pill">{outcomeLabel(caseData.outcome)}</div>
                  ) : null}
                </div>
              </div>
              {caseData.guided_progress?.steps?.length ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Guided steps</h3>
                  <p className="np-muted">Follow these steps in order. We store progress here. We will not submit a web form or drive a browser for you in this version.</p>
                  <ul className="np-list">
                    {caseData.guided_progress.steps.map((step) => (
                      <li key={step}>{step}</li>
                    ))}
                  </ul>
                  {caseData.guided_progress.form_url ? (
                    <p style={{ marginTop: 12 }}>
                      Start here: <a href={caseData.guided_progress.form_url} target="_blank" rel="noreferrer">{caseData.guided_progress.form_url}</a>
                    </p>
                  ) : null}
                </div>
              ) : null}
              {WAITING_STATES.includes(caseData.state) ? (
                <div className="np-card" style={{ marginTop: 16 }}>
                  <h3>Their reply</h3>
                  <p className="np-muted">Paste what the company sent back. We look for yes, no, or “need more info” — we do not guess. Store credit stays an offer until you choose.</p>
                  <textarea className="np-input" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Paste the company’s reply" />
                  <div className="np-actions">
                    <button type="button" className="np-btn ghost" disabled={busy || !reply.trim()} onClick={() => run(async () => {
                      await neveroverpayApi.merchantReply(caseData.id, reply);
                      setReply('');
                    })}>
                      Use this reply
                    </button>
                  </div>
                  <h3 style={{ marginTop: 20 }}>Bank statement</h3>
                  <p className="np-muted">Paste a CSV or a dated line such as 2026-09-03 AMAZON REFUND 32.19. Live Plaid match lives in Settings. We mark money recovered only when the refund match score is high enough.</p>
                  <textarea className="np-input" rows={3} value={proof} onChange={(e) => setProof(e.target.value)} placeholder="Date,Description,Amount" />
                  <div className="np-actions">
                    <button type="button" className="np-btn ghost" disabled={busy || !proof.trim()} onClick={() => run(async () => {
                      await neveroverpayApi.attachProof(caseData.id, proof);
                      setProof('');
                    })}>
                      This is my proof
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
        {approvalOpen && caseData && plan ? (
          <div className="np-modal-backdrop" role="dialog" aria-modal="true">
            <div className="np-card np-modal">
              <h2>{keepChoice ? 'Keep this plan?' : selectedChoice === 'human_help' ? 'Approve specialist follow-up?' : selectedChoice === 'draft_email' ? 'Approve this draft?' : 'Approve this action?'}</h2>
              {keepChoice ? (
                <>
                  <p style={{ marginTop: 12 }}>BilledTwice will not send an email. We will mark that you are keeping {caseData.merchant}.</p>
                  <p style={{ marginTop: 12 }}>{plan.summary}</p>
                </>
              ) : selectedChoice === 'human_help' ? (
                <>
                  <p style={{ marginTop: 12 }}>A recovery specialist will follow up with {plan.target || caseData.merchant} within these boundaries:</p>
                  <p>{executionPlan.purpose || plan.summary}</p>
                </>
              ) : selectedChoice === 'draft_email' ? (
                <>
                  <p style={{ marginTop: 12 }}>BilledTwice will not send this. Approving locks the exact draft you copy and send yourself.</p>
                  <p>{plan.summary}</p>
                </>
              ) : (
                <>
                  <p style={{ marginTop: 12 }}>BilledTwice will send one email to:</p>
                  <p><strong>{plan.target || 'the merchant support address in this draft'}</strong></p>
                  <p style={{ marginTop: 12 }}>It will request:</p>
                  <p>{caseData.approval_request || `The status of your ${caseData.estimated_display} refund for order ${caseData.order_number || 'this order'}.`}</p>
                </>
              )}
              <p style={{ marginTop: 16 }}>It will not:</p>
              <ul className="np-list">
                {(caseData.will_not || []).map((item) => <li key={item}>{item}</li>)}
              </ul>
              <div className="np-actions">
                <button
                  type="button"
                  className="np-btn"
                  disabled={busy}
                  onClick={() => run(async () => {
                    const row = await neveroverpayApi.approveAndSend(caseData.id, plan.content_hash);
                    setApprovalOpen(false);
                    setSentNotice(row.sent ? 'sent' : 'approved');
                  })}
                >
                  {keepChoice ? 'Confirm keep' : selectedChoice === 'human_help' ? 'Approve specialist follow-up' : selectedChoice === 'draft_email' ? 'Approve this draft' : 'Approve & Send'}
                </button>
                <button type="button" className="np-btn ghost" disabled={busy} onClick={() => setApprovalOpen(false)}>
                  Edit
                </button>
                <button type="button" className="np-btn ghost" disabled={busy} onClick={() => setApprovalOpen(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
